// =============================================================================
// Login Page - Identifier-first authentication (one door for sign in & sign up)
// =============================================================================
// v12 (2026-09-15): identifier-first per Sid — the page asks for email FIRST,
// then branches on /auth/check-user:
//   - account exists WITH a password  → password step (+ code alternative)
//   - account exists WITHOUT password → email OTP (legacy users, unchanged)
//   - no account                      → email OTP → verify auto-routes into
//                                       the signup form (existing machinery)
// So new users never see "user not found" — the same door signs them up.
// Phone-based users take the "Use phone number instead" path (OTP, and new
// phones likewise auto-route to signup after verification).
// v11 (2026-09): password auth v1; MFA step held in component state only so a
// mid-MFA refresh lands safely back at the email step.
// =============================================================================

import { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { LogIn, Mail, Phone, ArrowLeft, ArrowRight } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import { useSendCode, usePasswordLogin, useVerifyMfa } from '@/hooks/useAuth';
import { useAuthStore } from '@/stores/auth';
import { authApi } from '@/lib/auth';
import { PublicNavbar } from '@/components/PublicNavbar';
import { PageBackground } from '@/components/ui/PageBackground';
import { CodeInput, type CodeInputHandle } from '@/components/ui/CodeInput';

type LoginStep = 'email' | 'password' | 'phone';

const RESEND_COOLDOWN_SECONDS = 30;

export function LoginPage() {
  const [searchParams] = useSearchParams();

  const [step, setStep] = useState<LoginStep>('email');
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [localError, setLocalError] = useState('');

  // MFA state — component-local ONLY (never persisted): a refresh mid-MFA
  // returns to the email step, which is the safe behavior.
  const [mfa, setMfaState] = useState<{ maskedPhone: string } | null>(null);
  const [mfaError, setMfaError] = useState<string | null>(null);
  const [mfaAttemptsRemaining, setMfaAttemptsRemaining] = useState<number | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const codeInputRef = useRef<CodeInputHandle>(null);

  const { mutate: sendCode, isPending: isSendingCode } = useSendCode();
  const { error: storeError, setError: setStoreError, setReturnTo, setGuestPreFill } = useAuthStore();

  const passwordLogin = usePasswordLogin({
    onMfaRequired: ({ maskedPhone }) => {
      setStoreError(null);
      setMfaError(null);
      setMfaAttemptsRemaining(null);
      setMfaState({ maskedPhone });
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
    },
  });
  const verifyMfa = useVerifyMfa();

  // Identifier-first branch: check the account, then route.
  const checkUser = useMutation({
    mutationFn: (value: string) => authApi.checkUser(value),
    onSuccess: (result) => {
      if (result.exists && result.hasPassword) {
        setFirstName(result.firstName ?? null);
        setStep('password');
      } else {
        // Legacy user without a password OR brand-new email — both take the
        // OTP road; /verify auto-branches into login or the signup form.
        sendCode({ identifier: email, identifierType: 'email' });
      }
    },
    onError: () => setLocalError('Something went wrong. Please try again.'),
  });

  // v5.1: Capture returnTo from URL and store it for post-auth redirect
  // v5.2: Capture guest name/email for signup form pre-fill
  useEffect(() => {
    const returnTo = searchParams.get('returnTo');
    if (returnTo) {
      // Only allow internal paths (security: prevent open redirect)
      if (returnTo.startsWith('/')) {
        setReturnTo(returnTo);
      }
    }

    const guestName = searchParams.get('guestName');
    const guestEmail = searchParams.get('guestEmail');
    if (guestName || guestEmail) {
      setGuestPreFill({
        name: guestName || '',
        email: guestEmail || '',
      });
      if (guestEmail) setEmail(guestEmail);
    }
  }, [searchParams, setReturnTo, setGuestPreFill]);

  // MFA resend cooldown ticker
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setInterval(() => setResendCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [resendCooldown > 0]);

  const error = localError || storeError;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const clearErrors = () => {
    setLocalError('');
    setStoreError(null);
  };

  // ---------------------------------------------------------------------------
  // Step submissions
  // ---------------------------------------------------------------------------
  const handleEmailContinue = (e: React.FormEvent) => {
    e.preventDefault();
    clearErrors();
    if (!emailRegex.test(email)) {
      setLocalError('Please enter a valid email address');
      return;
    }
    checkUser.mutate(email);
  };

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    clearErrors();
    if (!password) {
      setLocalError('Password is required');
      return;
    }
    passwordLogin.mutate({ email, password });
  };

  const handlePhoneChange = (value: string) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 3) {
      setPhone(digits);
    } else if (digits.length <= 6) {
      setPhone(`(${digits.slice(0, 3)}) ${digits.slice(3)}`);
    } else {
      setPhone(`(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`);
    }
  };

  const handlePhoneSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    clearErrors();
    const digits = phone.replace(/\D/g, '');
    if (digits.length !== 10) {
      setLocalError('Please enter a valid 10-digit phone number');
      return;
    }
    sendCode({ identifier: phone, identifierType: 'phone' });
  };

  // ---------------------------------------------------------------------------
  // MFA code submit + resend
  // ---------------------------------------------------------------------------
  const handleMfaCode = (code: string) => {
    setMfaError(null);
    verifyMfa.mutate(
      { email, code },
      {
        onError: (err) => {
          const apiErr = err as { message?: string; data?: { error?: { attemptsRemaining?: number } } };
          setMfaError(apiErr.message || 'Incorrect verification code.');
          const remaining = apiErr.data?.error?.attemptsRemaining;
          if (typeof remaining === 'number') setMfaAttemptsRemaining(remaining);
          codeInputRef.current?.reset();
          codeInputRef.current?.focus();
        },
      }
    );
  };

  const handleMfaResend = () => {
    if (resendCooldown > 0 || passwordLogin.isPending) return;
    setMfaError(null);
    codeInputRef.current?.reset();
    passwordLogin.mutate({ email, password });
  };

  const inputClass = (hasError: boolean) =>
    `mt-1 block w-full px-3 py-2 border ${
      hasError ? 'border-red-500' : 'border-gray-300'
    } rounded-md shadow-sm focus:outline-none focus:ring-[#009FE4] focus:border-[#009FE4]`;

  const submitButtonClass =
    'w-full flex justify-center items-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gradient-to-r from-[#17DDAB] to-[#009FE4] hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#009FE4] disabled:opacity-50 disabled:cursor-not-allowed';

  // ---------------------------------------------------------------------------
  // MFA step view
  // ---------------------------------------------------------------------------
  if (mfa) {
    return (
      <PageBackground>
        <div className="min-h-screen">
          <PublicNavbar />
          <div className="flex flex-col justify-center py-12 sm:px-6 lg:px-8">
            <div className="sm:mx-auto sm:w-full sm:max-w-md">
              <h2 className="mt-6 text-center text-2xl font-semibold text-gray-900">
                Two-step verification
              </h2>
              <p className="mt-2 text-center text-sm text-gray-600">
                We sent a 6-digit code to {mfa.maskedPhone}
              </p>
            </div>
            <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
              <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
                <CodeInput
                  ref={codeInputRef}
                  onComplete={handleMfaCode}
                  isLoading={verifyMfa.isPending}
                  error={mfaError}
                  attemptsRemaining={mfaAttemptsRemaining}
                />
                <div className="mt-6 flex items-center justify-between text-sm">
                  <button
                    type="button"
                    onClick={() => {
                      setMfaState(null);
                      setMfaError(null);
                      setPassword('');
                      setStep('password');
                    }}
                    className="flex items-center gap-1 text-gray-500 hover:text-gray-700"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={handleMfaResend}
                    disabled={resendCooldown > 0 || passwordLogin.isPending}
                    className="text-[#009FE4] hover:underline disabled:text-gray-400 disabled:no-underline disabled:cursor-not-allowed"
                  >
                    {resendCooldown > 0 ? `Resend code (${resendCooldown}s)` : 'Resend code'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </PageBackground>
    );
  }

  const headings: Record<LoginStep, { title: string; subtitle: string }> = {
    email: {
      title: 'Sign in or create your account',
      subtitle: "Enter your email and we'll take you to the right place.",
    },
    password: {
      title: firstName ? `Welcome back, ${firstName}` : 'Welcome back',
      subtitle: 'Enter your password to sign in.',
    },
    phone: {
      title: 'Continue with phone',
      subtitle: "We'll text you a 6-digit verification code.",
    },
  };

  return (
    <PageBackground>
      <div className="min-h-screen">
        <PublicNavbar />

        <div className="flex flex-col justify-center py-12 sm:px-6 lg:px-8">
          <div className="sm:mx-auto sm:w-full sm:max-w-md">
            <h2 className="mt-6 text-center text-2xl font-semibold text-gray-900">
              {headings[step].title}
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">{headings[step].subtitle}</p>
          </div>

          <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
            <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
              {step === 'email' && (
                <>
                  <form onSubmit={handleEmailContinue} className="space-y-6">
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                        Email address
                      </label>
                      <input
                        type="email"
                        name="email"
                        id="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@example.com"
                        autoComplete="email"
                        autoFocus
                        className={inputClass(!!error)}
                      />
                      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
                    </div>

                    <button
                      type="submit"
                      disabled={checkUser.isPending || isSendingCode}
                      className={submitButtonClass}
                    >
                      {checkUser.isPending || isSendingCode ? (
                        'One moment...'
                      ) : (
                        <>
                          Continue
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </>
                      )}
                    </button>
                  </form>

                  <button
                    type="button"
                    onClick={() => {
                      clearErrors();
                      setStep('phone');
                    }}
                    className="mt-5 w-full flex items-center justify-center gap-2 text-sm text-gray-500 hover:text-gray-700"
                  >
                    <Phone className="w-4 h-4" />
                    Use phone number instead
                  </button>
                </>
              )}

              {step === 'password' && (
                <>
                  <div className="mb-5 flex items-center justify-between rounded-md bg-gray-50 px-3 py-2 text-sm">
                    <span className="text-gray-700 truncate">{email}</span>
                    <button
                      type="button"
                      onClick={() => {
                        clearErrors();
                        setPassword('');
                        setStep('email');
                      }}
                      className="ml-3 flex-shrink-0 text-[#009FE4] hover:underline"
                    >
                      Change
                    </button>
                  </div>

                  <form onSubmit={handlePasswordSubmit} className="space-y-5">
                    <div>
                      <div className="flex items-center justify-between">
                        <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                          Password
                        </label>
                        <Link to="/forgot-password" className="text-sm text-[#009FE4] hover:underline">
                          Forgot password?
                        </Link>
                      </div>
                      <input
                        type="password"
                        name="password"
                        id="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="••••••••••••"
                        autoComplete="current-password"
                        autoFocus
                        className={inputClass(!!error)}
                      />
                    </div>

                    {error && <p className="text-sm text-red-600">{error}</p>}

                    <button type="submit" disabled={passwordLogin.isPending} className={submitButtonClass}>
                      {passwordLogin.isPending ? (
                        'Signing in...'
                      ) : (
                        <>
                          <LogIn className="w-4 h-4 mr-2" />
                          Sign in
                        </>
                      )}
                    </button>
                  </form>

                  <button
                    type="button"
                    onClick={() => {
                      clearErrors();
                      sendCode({ identifier: email, identifierType: 'email' });
                    }}
                    disabled={isSendingCode}
                    className="mt-5 w-full flex items-center justify-center gap-2 text-sm text-gray-500 hover:text-gray-700 disabled:text-gray-300"
                  >
                    <Mail className="w-4 h-4" />
                    {isSendingCode ? 'Sending code...' : 'Email me a code instead'}
                  </button>
                </>
              )}

              {step === 'phone' && (
                <>
                  <form onSubmit={handlePhoneSubmit} className="space-y-6">
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                        Phone number
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        id="phone"
                        value={phone}
                        onChange={(e) => handlePhoneChange(e.target.value)}
                        placeholder="(555) 123-4567"
                        autoComplete="tel"
                        autoFocus
                        className={inputClass(!!error)}
                      />
                      <p className="mt-2 text-sm text-gray-500">
                        We&apos;ll send you a verification code via SMS
                      </p>
                      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
                    </div>

                    <button type="submit" disabled={isSendingCode} className={submitButtonClass}>
                      {isSendingCode ? (
                        'Sending...'
                      ) : (
                        <>
                          <LogIn className="w-4 h-4 mr-2" />
                          Send Verification Code
                        </>
                      )}
                    </button>
                  </form>

                  <button
                    type="button"
                    onClick={() => {
                      clearErrors();
                      setStep('email');
                    }}
                    className="mt-4 w-full flex items-center justify-center gap-2 text-sm text-gray-500 hover:text-gray-700"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Use email instead
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </PageBackground>
  );
}
